import React from "react";
import { withStyles } from "@material-ui/core";
import DataGrid from "../../../../dataGrid";
import StatusBar from "../../../../dataGrid/StatusBar";
import GridConfig from "./gridConfig";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import { DateTimeEditor } from "../../../../dataGrid/cellEditors";
import appConstant from "../../../../../appConstants";
import {
  getUTCDate,
  formatMonthDateYear,
} from "../../../../dataGrid/gridUtilities";
import { isValid as isDateTimeValid, format } from "date-fns";
import { getDataWithQueryString } from "../../../../../api/componentAPIs";
import Button from "@material-ui/core/Button";
import EventIcon from "@material-ui/icons/Event";
import { compose } from "recompose";
import CircularProgress from "@material-ui/core/CircularProgress";
import Events from "../../../../../utilities/EventEmitter";
import { withSnackbar } from "notistack";
import MESSAGES from "../../../../../appMessages";
import ClearAllFilterButton from "../../../../dataGrid/columnFilters/ClearAllFilterButton";

const styles = (theme) => ({
  header: {
    padding: "4px 16px",
    display: "flex",
    alignItems: "center",
  },
  headerText: {
    paddingRight: "15px",
    fontWeight: 600,
    fontSize: 16,
    color: theme.palette.primary.main,
  },
  complianceGridContainer: {
    boxSizing: "border-box",
    margin: "0px 16px",
    border: `1px solid ${theme.AppBGColor}`,
    height: "calc(100% - 40px)",
    "& .ag-theme-material": {
      borderBottom: `1px solid ${theme.AppBGColor}`,
    },
    "&>.gridTheme-light,&>.gridTheme-dark": {
      height: "100%",
    },
  },
  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  noDataText: {
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.iconColor,
  },
  noDataSubText: {
    fontSize: theme.textFontSize,
    lineHeight: "16px",
    color: theme.iconColor,
    paddingTop: "15px",
  },
  centerText: {
    textAlign: "center",
  },
  leftIcon: {
    marginRight: 10,
    fontSize: 20,
  },
  buttonStyle: {
    fontSize: theme.textFontSize,
    marginTop: "20px",
    height: "30px",
    boxShadow: "none",
    padding: "10px",
  },
  clearAllFilterButton: {
    marginLeft: "auto"
  }
});

class NextStopComplianceGrid extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig();
    this.terminalCode = this.props.userData.userProfile.terminal.code;
    this.state = {
      gridApi: null,
      dispatchDate: null,
      allRouteComplianceData: [],
      gridUpdateInProgress: false,
    };
    this.NEXT_STOP_COMPLIANCE_DATE_FORMAT = "yyyy-MM-dd";
  }

  componentDidMount = () => {
    if (!this.props.nextStopComplianceDate) {
      this.getLatestNextStopComplianceReportDate();
    } else {
      this.updateDate(this.props.nextStopComplianceDate);
    }
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.userData.userProfile.terminal.code !==
      prevProps.userData.userProfile.terminal.code
    ) {
      this.getLatestNextStopComplianceReportDate();
    }
  }

  getLatestNextStopComplianceReportDate = () => {
    // get latest date which has compliance Data
    const terminalCode = this.props.userData.userProfile.terminal.code;
    this.setState({ gridUpdateInProgress: true });
    getDataWithQueryString(
      "GetLatestNSCReportDate",
      `terminalCode=${terminalCode}`
    ).then(
      (response) => {
        if (response.status >= 200 && response.status <= 400) {
          const dispatchDate = response.data.latestNSCReportDate || new Date();
          this.updateDate(dispatchDate);
        } else {
          this.updateDate(new Date());
        }
      },
      (error) => {
        this.updateDate(new Date());
      }
    );
  };

  getComplianceData = (date) => {
    const dispatchDate = format(
      getUTCDate(date),
      this.NEXT_STOP_COMPLIANCE_DATE_FORMAT
    );
    this.terminalCode = this.props.userData.userProfile.terminal.code;
    if (dispatchDate && this.terminalCode) {
      this.setState({ gridUpdateInProgress: true });
      getDataWithQueryString(
        "GetNextStopComplianceGridData",
        `dispatchDate=${dispatchDate}&terminalCode=${this.terminalCode}`
      ).then(
        (response) => {
          if (response.status >= 200 && response.status <= 400) {
            this.setState({
              allRouteComplianceData: response.data,
              gridUpdateInProgress: false,
            });
            Events.emit(
              "getRouteComplianceKPI",
              dispatchDate,
              this.terminalCode
            );
          } else {
            this.handleError(dispatchDate, this.terminalCode);
          }
        },
        (error) => {
          this.handleError(dispatchDate, this.terminalCode);
        }
      );
    }
  };

  handleError = (dispatchDate, terminalCode) => {
    this.props.enqueueSnackbar(
      MESSAGES.NEXT_STOP_COMPLIANCE.FETCH_DATA_FAILURE,
      { variant: "error" }
    );
    this.setState({
      allRouteComplianceData: [],
      gridUpdateInProgress: false,
    });
    Events.emit("getRouteComplianceKPI", dispatchDate, terminalCode);
  };

  onGridReady = ({ api }) => {
    this.setState({
      gridApi: api
    })
  };

  constructStatusBar = (allRouteComplianceData) => {
    return [
      {
        label: allRouteComplianceData.length + " Active Routes",
        filterCondition: "",
      },
    ];
  };

  onSelectionChanged = (params) => {
    this.props.openMapView(params.data, this.state.allRouteComplianceData);
  };

  onDateChanged = (value) => {
    if (
      value &&
      isDateTimeValid(value) &&
      format(
        getUTCDate(this.state.dispatchDate),
        this.NEXT_STOP_COMPLIANCE_DATE_FORMAT
      ) !== format(getUTCDate(value), this.NEXT_STOP_COMPLIANCE_DATE_FORMAT)
    ) {
      this.updateDate(value);
    }
  };

  showPreviousDateData = () => {
    const previousDate = new Date(this.state.dispatchDate);
    previousDate.setDate(previousDate.getDate() - 1);
    this.updateDate(previousDate);
  };

  updateDate = (date) => {
    this.setState({
      dispatchDate: date,
    });
    this.props.updateNextStopComplianceDate(date);
    this.getComplianceData(date);
  };

  sortAllRouteComplianceData = (data) => {
    return data.sort((a, b) =>
      a.compliancePercentage > b.compliancePercentage ? -1 : 1
    );
  };

  render() {
    const { classes } = this.props;
    const { allRouteComplianceData, gridUpdateInProgress } = this.state;
    const rowData = this.sortAllRouteComplianceData(allRouteComplianceData);
    const dispatchDate = formatMonthDateYear({
      value: this.state.dispatchDate,
    });
    return (
      <React.Fragment>
        <div> </div>
        <header className={classes.header}>
          <span className={classes.headerText}>Next Stop Compliance</span>

          <DateTimeEditor
            dateType={appConstant.DATETYPES.MONTHDATEYEAR}
            onChange={this.onDateChanged}
            disableFuture={true}
            value={dispatchDate}
          />
          <div className={classes.clearAllFilterButton}>
            <ClearAllFilterButton gridApi={this.state.gridApi}/>
          </div>
        </header>
        <div className={classes.complianceGridContainer}>
          {allRouteComplianceData.length ? (
            <DataGrid
              defaultColDef={this.gridConfig.config.defaultColDef}
              columnDefs={this.gridConfig.config.columns}
              columnTypes={this.gridConfig.config.columnTypes}
              config={this.gridConfig.config}
              onGridReady={this.onGridReady}
              enableFilter={this.gridConfig.config.enableFilter}
              enableSorting={this.gridConfig.config.enableSorting}
              rowData={rowData}
              panelLabel={(filteredData) => (
                <StatusBar
                  data={this.constructStatusBar(filteredData)}
                />
              )}
              onCellDoubleClicked={this.onSelectionChanged}
            />
          ) : (
            <div className={classes.noData}>
              {gridUpdateInProgress && <CircularProgress size={40} />}
              {!gridUpdateInProgress && (
                <div className={classes.centerText}>
                  <div className={classes.noDataText}>No data available</div>
                  <div className={classes.noDataSubText}>
                    Data will be displayed once a driver has completed a trip.
                  </div>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    className={classes.buttonStyle}
                    onClick={this.showPreviousDateData}
                  >
                    <EventIcon
                      className={`${classes.leftIcon} ${classes.iconSmall}`}
                    />
                    Show previous data
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default compose(
  withStyles(styles),
  withUserProfile,
  withSnackbar
)(NextStopComplianceGrid);
